import React, { useLayoutEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { Sun, Moon, Menu, X } from 'lucide-react';
import { gsap, EASE, prefersReducedMotion } from "../public/gsap";

const NAV_LINKS = [
    { to: "/about", label: "About" },
    { to: "/pricing", label: "Pricing" },
    { to: "/resources", label: "Resources" },
];

const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
});

// Journal masthead: wordmark and links, a double rule, then a dateline.
// It scrolls away with the page like the top of a printed report.
// Motion: the double rule draws in on each page load (one line, then the
// other), and a red hairline at the top of the viewport tracks reading
// progress down the page.
function Navbar({ isOpen, setIsOpen }) {
    const { theme, toggleTheme } = useTheme();
    const { isAuthenticated } = useAuth();
    const mastRef = useRef(null);
    const progressRef = useRef(null);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            if (prefersReducedMotion()) return;
            gsap.timeline()
                .fromTo(".masthead-rule", { scaleX: 0 }, { scaleX: 1, duration: 1.4, ease: EASE, stagger: 0.18 })
                .fromTo(".masthead-dateline > *", { autoAlpha: 0, y: -4 }, { autoAlpha: 1, y: 0, duration: 0.8, ease: EASE, stagger: 0.1 }, 0.5);
        }, mastRef);
        return () => ctx.revert();
    }, []);

    // Reading progress. Measured from the document each frame, so it stays
    // right when content loads in late (pricing plans, status rows).
    useLayoutEffect(() => {
        const bar = progressRef.current;
        if (!bar) return;
        let frame = 0;
        const update = () => {
            frame = 0;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
            bar.style.transform = `scaleX(${p})`;
        };
        const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, []);

    const linkClass = ({ isActive }) =>
        `link-underline font-plex text-[14px] transition-colors ${
            isActive ? "text-ink dark:text-paper" : "text-ink/60 hover:text-ink dark:text-paper/60 dark:hover:text-paper"
        }`;

    return (
        <div ref={mastRef} className="text-ink dark:text-paper">
            <div
                ref={progressRef}
                aria-hidden="true"
                className="fixed inset-x-0 top-0 z-50 h-[2px] origin-left bg-stamp print:hidden"
                style={{ transform: "scaleX(0)" }}
            />
            <nav className="flex h-16 items-center justify-between">
                <Link to="/" className="flex items-baseline gap-3">
                    <span className="font-news text-[30px] font-medium leading-none tracking-tight">JPRO</span>
                    <span className="hidden sm:inline font-plex-mono text-[11px] uppercase tracking-[0.2em] text-ink/50 dark:text-paper/50">
                        Compliance intelligence
                    </span>
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map(({ to, label }) => (
                        <NavLink key={to} to={to} className={linkClass}>{label}</NavLink>
                    ))}
                    <span aria-hidden="true" className="h-4 w-px bg-ink/20 dark:bg-paper/20" />
                    {isAuthenticated ? (
                        <NavLink to="/dashboard" className={linkClass}>Dashboard</NavLink>
                    ) : (
                        <>
                            <NavLink to="/login" className={linkClass}>Sign in</NavLink>
                            <Link
                                to="/?ask=1"
                                className="link-underline font-plex-mono text-[12px] font-medium uppercase tracking-[0.18em] text-stamp"
                            >
                                JPRO
                            </Link>
                        </>
                    )}
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle theme"
                        className="text-ink/50 hover:text-ink dark:text-paper/50 dark:hover:text-paper transition-colors"
                    >
                        {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                </div>

                <button
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isOpen}
                    className="md:hidden p-1"
                >
                    {isOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
            </nav>

            <div aria-hidden="true" className="text-ink/80 dark:text-paper/70">
                <div className="masthead-rule h-px origin-left bg-current" />
                <div className="h-[3px]" />
                <div className="masthead-rule h-px origin-left bg-current" />
            </div>

            <div className="masthead-dateline flex items-center justify-between py-2 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-ink/50 dark:text-paper/50">
                <span>{today}</span>
                <span className="hidden sm:inline">Intelligent compliance for affordable housing</span>
            </div>

            {/* Mobile menu */}
            <div
                className={`md:hidden overflow-hidden border-ink/15 dark:border-paper/15 transition-[max-height] duration-500 ease-out ${
                    isOpen ? "max-h-96 border-b" : "max-h-0"
                }`}
            >
                <div className="flex flex-col gap-4 pb-6 pt-2 font-plex text-base">
                    {NAV_LINKS.map(({ to, label }) => (
                        <Link key={to} to={to}>{label}</Link>
                    ))}
                    {isAuthenticated ? (
                        <Link to="/dashboard">Dashboard</Link>
                    ) : (
                        <>
                            <Link to="/login">Sign in</Link>
                            <Link to="/?ask=1" className="font-plex-mono text-[13px] uppercase tracking-[0.18em] text-stamp">
                                Ask JPRO
                            </Link>
                        </>
                    )}
                    <button onClick={toggleTheme} className="flex items-center gap-2 text-ink/60 dark:text-paper/60">
                        {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                        {theme === 'dark' ? "Light mode" : "Dark mode"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Navbar;
